import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingCart } from "lucide-react";
import { cartPermalink } from "@/lib/quizConfig";
import { useIsEmbedded } from "@/lib/embed";

const Row = ({ label, a, b }) => (
  <div className="cd-compare-spec-row">
    <div className="label">{label}</div>
    <div style={{ fontSize: 14, fontWeight: 500 }}>{a || "—"}</div>
    <div style={{ fontSize: 14, fontWeight: 500 }}>{b || "—"}</div>
  </div>
);

export default function Compare() {
  const location = useLocation();
  const navigate = useNavigate();
  const embedded = useIsEmbedded();
  const paddles = location.state?.paddles || [];

  if (paddles.length !== 2) {
    return (
      <div className="cd-container" data-testid="compare-empty">
        <div className="cd-card" style={{ padding: 24, textAlign: "center" }}>
          <h2 className="cd-h2">Pick 2 paddles first</h2>
          <p className="cd-sub" style={{ marginTop: 8 }}>Go back to results and select two paddles to compare.</p>
          <button className="cd-btn-primary" style={{ marginTop: 16 }} onClick={() => navigate("/results")}>
            Back to results
          </button>
        </div>
      </div>
    );
  }

  const [p1, p2] = paddles;
  const a1 = p1.attributes || {};
  const a2 = p2.attributes || {};

  return (
    <div className="cd-container-wide cd-fade-in" data-testid="compare-page">
      <button className="cd-btn-ghost" onClick={() => navigate("/results")} data-testid="compare-back-btn" style={{ marginBottom: 12 }}>
        <ArrowLeft size={14} style={{ marginRight: 6 }} /> Back to results
      </button>

      <h1 className="cd-h1" style={{ marginBottom: 18 }}>Side-by-side</h1>

      <div className="cd-compare-grid" data-testid="compare-grid">
        {paddles.map((p) => {
          const v = (p.variants || []).find((x) => x.available) || (p.variants || [])[0];
          return (
            <div key={p.product_id} className="cd-card" style={{ overflow: "hidden" }} data-testid={`compare-card-${p.product_id}`}>
              <div className="cd-result-image-wrap">
                {p.image && <img src={p.image} alt={p.title} />}
              </div>
              <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.3 }}>{p.title}</div>
                <div className="cd-money" style={{ fontSize: 18 }}>${p.price?.toFixed(2)}</div>
                {v && (
                  <a href={cartPermalink(v.variant_id, 1)} target={embedded ? "_top" : "_blank"} rel="noreferrer" className="cd-btn-primary" style={{ marginTop: 8, textDecoration: "none" }} data-testid={`compare-atc-${p.product_id}`}>
                    <ShoppingCart size={16} /> Add to cart
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 24 }}>
        <Row label="Match score" a={`${p1.score}%`} b={`${p2.score}%`} />
        <Row label="Weight" a={a1.weight_oz ? `${a1.weight_oz} oz` : null} b={a2.weight_oz ? `${a2.weight_oz} oz` : null} />
        <Row label="Thickness" a={a1.thickness_mm ? `${a1.thickness_mm} mm` : null} b={a2.thickness_mm ? `${a2.thickness_mm} mm` : null} />
        <Row label="Core" a={a1.core} b={a2.core} />
        <Row label="Surface" a={a1.surface} b={a2.surface} />
        <Row label="Control feel" a={a1.play_style_scores ? `${(a1.play_style_scores.control * 100).toFixed(0)}%` : null} b={a2.play_style_scores ? `${(a2.play_style_scores.control * 100).toFixed(0)}%` : null} />
        <Row label="Power" a={a1.play_style_scores ? `${(a1.play_style_scores.power * 100).toFixed(0)}%` : null} b={a2.play_style_scores ? `${(a2.play_style_scores.power * 100).toFixed(0)}%` : null} />
        <Row label="Spin" a={a1.play_style_scores ? `${(a1.play_style_scores.spin * 100).toFixed(0)}%` : null} b={a2.play_style_scores ? `${(a2.play_style_scores.spin * 100).toFixed(0)}%` : null} />
        <Row label="Vendor" a={p1.vendor} b={p2.vendor} />
        <Row label="Availability" a={p1.available ? "In stock" : "Sold out"} b={p2.available ? "In stock" : "Sold out"} />
      </div>

      {p1.reason && p2.reason && (
        <div style={{ marginTop: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div className="cd-result-reason">Why {p1.title.split(" ")[0]}: {p1.reason}.</div>
          <div className="cd-result-reason">Why {p2.title.split(" ")[0]}: {p2.reason}.</div>
        </div>
      )}
    </div>
  );
}
